import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Dropdown, Input, InputNumber, Space, Tooltip, Typography } from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  ArrowRightOutlined,
  SwapOutlined,
  CloseOutlined,
} from "@ant-design/icons";

import { ItemSchemaGroup } from "../../types/model";
import { useAntdTheme } from "../../shared/hooks";
import { PointsConfig } from "../points-config";
import { ItemPicker, ItemThumb, MoveButtons, tierLabel } from "./components";
import {
  MenuItemIndex,
  TierOptionPrice,
  centsToDollars,
  dollarsToCents,
  fallbackPoints,
  formatAdjustment,
  formatCents,
  moveInArray,
} from "./pricing";
import { ComboAddonTier } from "./types";

const { Text } = Typography;

type TierCardProps = {
  tier: ComboAddonTier;
  index: number;
  tiers: ReadonlyArray<ComboAddonTier>;
  groups: ReadonlyArray<ItemSchemaGroup>;
  items: MenuItemIndex;
  pointsConfig: PointsConfig | undefined;
  // Options of this tier priced by an override, for the summary line.
  overriddenOptions: TierOptionPrice[];
  onChange: (tier: ComboAddonTier) => void;
  onMove: (from: number, to: number) => void;
  onDuplicate: () => void;
  onRemove: () => void;
  onMoveItem: (itemSchemaId: string, toTierId: string) => void;
};

export const TierCard: React.FC<TierCardProps> = ({
  tier,
  index,
  tiers,
  groups,
  items,
  pointsConfig,
  overriddenOptions,
  onChange,
  onMove,
  onDuplicate,
  onRemove,
  onMoveItem,
}) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();

  const fallback = fallbackPoints(tier.price, pointsConfig);
  const pointsPlaceholder =
    fallback !== undefined
      ? t("combo_points_default", { points: fallback })
      : t("combo_not_redeemable");

  const setItems = (ids: string[]) =>
    onChange({ ...tier, items: ids.map((id, seq) => ({ item_schema_id: id, seq })) });

  const otherTiers = tiers.filter((other) => other.id !== tier.id);

  const summary =
    overriddenOptions.length === 0
      ? t("combo_tier_regular_option_prices")
      : overriddenOptions
          .map((o) =>
            o.cents === 0
              ? t("combo_option_free", { name: o.choice.description })
              : `${o.choice.description} ${formatAdjustment(o.cents)}`
          )
          .join(", ");

  return (
    <Card
      size="small"
      data-testid={`combo-tier-${index}`}
      style={{ borderRadius: token.borderRadiusLG, marginBottom: token.marginMD }}
      title={
        <Space wrap>
          <Input
            aria-label={t("combo_tier_label")}
            placeholder={t("combo_tier_default_name", { price: formatCents(tier.price) })}
            value={tier.name ?? ""}
            onChange={(e) => onChange({ ...tier, name: e.target.value })}
            style={{ width: 180 }}
          />
          <InputNumber<string>
            aria-label={t("combo_tier_price")}
            prefix="$"
            min="0"
            step="0.01"
            precision={2}
            stringMode
            value={centsToDollars(tier.price)}
            onChange={(v) => onChange({ ...tier, price: Math.max(0, dollarsToCents(v) ?? 0) })}
            style={{ width: 120 }}
          />
          <Tooltip title={t("combo_tier_points_help")}>
            <InputNumber<number>
              aria-label={t("points")}
              min={0}
              step={1}
              precision={0}
              value={tier.points ?? null}
              placeholder={pointsPlaceholder}
              onChange={(v) => onChange({ ...tier, points: v ?? null })}
              suffix={t("combo_pts")}
              style={{ width: 210 }}
            />
          </Tooltip>
        </Space>
      }
      extra={
        <Space size={0}>
          <MoveButtons index={index} count={tiers.length} onMove={onMove} />
          <Tooltip title={t("combo_duplicate_tier")}>
            <Button
              type="text"
              size="small"
              aria-label={t("combo_duplicate_tier")}
              icon={<CopyOutlined />}
              onClick={onDuplicate}
            />
          </Tooltip>
          <Tooltip title={t("combo_remove_tier")}>
            <Button
              type="text"
              size="small"
              danger
              aria-label={t("combo_remove_tier")}
              icon={<DeleteOutlined />}
              onClick={onRemove}
            />
          </Tooltip>
        </Space>
      }
    >
      <ItemPicker
        groups={groups}
        value={tier.items.map((ti) => ti.item_schema_id)}
        onChange={setItems}
        testId={`combo-tier-${index}-picker`}
      />
      {tier.items.length === 0 ? (
        <Text type="secondary" style={{ display: "block", marginTop: token.marginSM }}>
          {t("combo_tier_no_items")}
        </Text>
      ) : (
        <div style={{ marginTop: token.marginSM }}>
          {tier.items.map((ti, i) => {
            const ref = items.get(ti.item_schema_id);
            return (
              <div
                key={ti.item_schema_id}
                data-testid="combo-tier-item"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: token.marginSM,
                  padding: `${token.paddingXXS}px 0`,
                  borderBottom: `1px solid ${token.colorBorderSecondary}`,
                }}
              >
                <ItemThumb item={ref?.item} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text strong={!!ref} type={ref ? undefined : "danger"}>
                    {ref ? ref.item.name : t("combo_item_not_on_menu")}
                  </Text>
                  {ref ? (
                    <Text type="secondary" style={{ display: "block", fontSize: token.fontSizeSM }}>
                      {t("combo_regular_price_in_combo", {
                        regular: formatCents(ref.item.price),
                        combo: formatCents(tier.price),
                      })}
                    </Text>
                  ) : null}
                </div>
                {otherTiers.length > 0 ? (
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: otherTiers.map((other) => ({
                        key: other.id,
                        icon: <ArrowRightOutlined />,
                        label: tierLabel(t, other),
                      })),
                      onClick: ({ key }) => onMoveItem(ti.item_schema_id, key),
                    }}
                  >
                    <Button type="text" size="small" icon={<SwapOutlined />}>
                      {t("combo_move_to_tier")}
                    </Button>
                  </Dropdown>
                ) : null}
                <MoveButtons
                  index={i}
                  count={tier.items.length}
                  onMove={(from, to) =>
                    setItems(
                      moveInArray(
                        tier.items.map((x) => x.item_schema_id),
                        from,
                        to
                      )
                    )
                  }
                />
                <Button
                  type="text"
                  size="small"
                  aria-label={t("remove")}
                  icon={<CloseOutlined />}
                  onClick={() =>
                    setItems(
                      tier.items
                        .map((x) => x.item_schema_id)
                        .filter((id) => id !== ti.item_schema_id)
                    )
                  }
                />
              </div>
            );
          })}
        </div>
      )}
      <Text
        type="secondary"
        data-testid="combo-tier-summary"
        style={{ display: "block", marginTop: token.marginSM, fontSize: token.fontSizeSM }}
      >
        {t("combo_tier_options_summary", { summary })}
      </Text>
    </Card>
  );
};
